import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * BrandingContext — site-wide brand controls owned by Admin.
 *
 * Loads /api/branding on mount and applies the active font_stack onto a
 * CSS custom property `--font-brand` on the <html> element. Components
 * that want to use the configurable brand font set `font-family: var(--font-brand, inherit)`.
 *
 * Exposed:
 *   - branding: { font_key, font_stack, logo_url, wordmark, show_wordmark }
 *   - logoSrc: absolute URL for the logo (handles /api/... prefix → backend URL)
 *   - refresh(): re-fetches /api/branding (call after admin saves)
 */
const BrandingContext = createContext({
  branding: null,
  logoSrc: null,
  refresh: () => {},
});

const DEFAULT_BRANDING = {
  font_key: "system",
  font_stack: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto",
  logo_url: null,
  wordmark: "Born Day",
  show_wordmark: true,
  tagline: "Your calendar",
  slogan: "Every day. Every person. All in one place.",
};

function resolveAsset(path) {
  if (!path) return null;
  if (path.startsWith("/api/")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${path}`;
  }
  return path;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/branding");
      setBranding({ ...DEFAULT_BRANDING, ...data });
    } catch {
      setBranding(DEFAULT_BRANDING);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply font_stack to a single CSS variable so components opt in cleanly.
  useEffect(() => {
    if (!branding?.font_stack) return;
    document.documentElement.style.setProperty("--font-brand", branding.font_stack);
  }, [branding?.font_stack]);

  const value = {
    branding,
    logoSrc: resolveAsset(branding?.logo_url),
    refresh,
  };
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}
